<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/packing/new_package.yml"
  lang="yaml"
></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_harvested_batch')"
    :params="{
      organization_id: organization_id,
      harvested_batch_id: harvested_batch_id,
    }"
  />

  <bread-crumbs
    :labels="[
      module_name,
      harvested_batch.harvest_process?.name,
      harvested_batch.batch_code,
    ]"
  />

  <template v-if="load_general_data">
    <harvested-batch-info :harvested_batch="harvested_batch" />

    <alert-danger class="mt-8" :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.producer") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="producers_crop_fields"
                    v-model="producer_crop_field_id"
                    label="label"
                    :is_invalid="isInvalidSelect('producer')"
                  />
                </div>
                <div class="space-x-2 mt-2">
                  <template
                    v-for="(certification, index) in crop_field_certifications"
                    :key="index"
                  >
                    <danger-badge
                      v-if="availableCertification(certification)"
                      :label="certification.certification.name"
                    />
                    <success-badge
                      v-else
                      :label="certification.certification.name"
                    />
                  </template>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package.quantity") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="pkg.quantity"
                    :class="['input-text', isInvalid('quantity')]"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_categories.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_category") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_categories"
                    v-model="pkg.exporter_process_crop_category_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_category')
                    "
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_varieties.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_variety") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_varieties"
                    v-model="pkg.exporter_process_crop_variety_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_variety')
                    "
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_sizes.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_size") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_sizes"
                    v-model="pkg.exporter_process_crop_size_id"
                    :is_invalid="isInvalidSelect('exporter_process_crop_size')"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_packages.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_crop_package") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_crop_packages"
                    v-model="pkg.exporter_process_crop_package_id"
                    :is_invalid="
                      isInvalidSelect('exporter_process_crop_package')
                    "
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_crop_packages.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.buy_price_per_package") }}
                </label>
                <div class="mt-1 flex w-full">
                  <select-form
                    class="w-1/3"
                    :options="currencies"
                    v-model="pkg.buy_price_currency_id"
                    :disabled="currencies.length <= 1"
                    :is_invalid="isInvalidSelect('currency')"
                  ></select-form>
                  <div class="flex flex-1 relative">
                    <div
                      class="flex items-center absolute inset-y-0 left-0 ml-3 text-sm leading-5"
                    >
                      {{ currency_symbol }}
                    </div>
                    <input
                      type="number"
                      v-model.number="pkg.buy_price_per_package"
                      disabled
                      :class="[
                        'input-text pl-6 disabled:bg-gray-50',
                        isInvalid('buy_price_per_package'),
                      ]"
                    />
                  </div>
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="exporter_process_brands.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.package.exporter_process_brand") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="exporter_process_brands"
                    v-model="pkg.exporter_process_brand_id"
                    :is_invalid="isInvalidSelect('exporter_process_brand')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3" v-if="packing_lines.length > 1">
                <label class="label-form">
                  {{ $t("model.package.packing_line") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="packing_lines"
                    v-model="pkg.packing_line_id"
                    :is_invalid="isInvalidSelect('packing_line')"
                  />
                </div>
              </div>
              <div
                class="sm:col-span-3"
                v-if="packing_house_processes.length > 1"
              >
                <label class="label-form">
                  {{ $t("model.exported_batch.packing_house_process") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="packing_house_processes"
                    v-model="pkg.exported_batch.packing_house_process_id"
                    :is_invalid="
                      isInvalidSelect('exported_batch.packing_house_process')
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowHarvestedBatch()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!charging_submit"
              @click.prevent="create()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/process/packing/manage_package.js";
import RequesterQuery from "@/views/organization/requesters/process/packing/queries_to_package.js";
import HarvestedBatchInfo from "@/components/views/organization/process/packing/HarvestedBatchInfo.vue";

export default {
  name: "OrgProcessPackingNewPackage",
  components: { HarvestedBatchInfo },
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvested_batch: {},
      producers_crop_fields: [],
      exporter_process_crop_packages: [],
      exporter_process_crop_categories: [],
      exporter_process_crop_varieties: [],
      exporter_process_crop_sizes: [],
      exporter_process_brands: [],
      packing_lines: [],
      packing_house_processes: [],
      producer_crop_field_id: null,
      pkg: {
        producer_id: null,
        exporter_process_crop_package_id: null,
        exporter_process_crop_category_id: null,
        exporter_process_crop_variety_id: null,
        exporter_process_crop_size_id: null,
        exporter_process_brand_id: null,
        packing_line_id: null,
        quantity: null,
        exported_batch: {
          packing_house_process_id: null,
          crop_field_id: null,
        },
      },
      crop_field_certifications: [],
      currencies: [],
      currency_symbol: null,
    };
  },
  props: {
    organization_id: { required: true },
    harvested_batch_id: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  computed: {},
  methods: {
    loadData: async function () {
      await this.fetchCurrencies();
      await this.fetchProducersCropFields();
      await this.fetchHarvestedBatch();
      await this.fetchPackingLines();
      await this.fetchExporterProcessCropCategories();
      await this.fetchExporterProcessCropVarieties();
      await this.fetchExporterProcessCropSizes();
      await this.fetchExporterProcessCropPackages();
      await this.fetchExporterProcessBrands();
      await this.fetchPackingHouseProcesses();
      this.load_general_data = true;
    },
    fetchHarvestedBatch: async function () {
      const response = await Requester.fetchHarvestedBatch(
        this.organization_id,
        this.harvested_batch_id
      );
      this.harvested_batch = response.data;
      this.harvest_process_id = this.harvested_batch.harvest_process_id;
      const producer_crop_field = this.producers_crop_fields.find(
        (prod) =>
          prod.id ==
          `${this.harvested_batch.producer_id}_${this.harvested_batch.crop_field_id}`
      );
      if (producer_crop_field) {
        this.producer_crop_field_id = `${this.harvested_batch.producer_id}_${this.harvested_batch.crop_field_id}`;
      }
    },
    fetchProducersCropFields: async function () {
      const response = await RequesterQuery.fetchProducersCropFields(
        this.organization_id,
        this.harvested_batch_id
      );
      this.producers_crop_fields = response.data;
    },
    fetchCurrencies: async function () {
      const response = await RequesterQuery.fetchCurrencies(
        this.organization_id,
        this.harvested_batch_id
      );
      this.currencies = response.data;
      if (this.currencies.length == 1)
        this.pkg.buy_price_currency_id = this.currencies[0].id;
    },
    fetchPackingLines: async function () {
      const response = await RequesterQuery.fetchPackingLines(
        this.organization_id,
        this.harvested_batch_id
      );
      this.packing_lines = response.data;
      this.pkg.packing_line_id =
        this.hasOneItem(this.packing_lines) ||
        this.harvested_batch.packing_line_id;
    },
    fetchExporterProcessCropCategories: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropCategories(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_categories = response.data;
      this.pkg.exporter_process_crop_category_id = this.hasOneItem(
        this.exporter_process_crop_categories
      );
    },
    fetchExporterProcessCropVarieties: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropVarieties(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_varieties = response.data;
      this.pkg.exporter_process_crop_variety_id = this.hasOneItem(
        this.exporter_process_crop_varieties
      );
    },
    fetchExporterProcessCropSizes: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropSizes(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_sizes = response.data;
      this.pkg.exporter_process_crop_size_id = this.hasOneItem(
        this.exporter_process_crop_sizes
      );
    },
    fetchExporterProcessCropPackages: async function () {
      const response = await RequesterQuery.fetchExporterProcessCropPackages(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_crop_packages = response.data;
      this.pkg.exporter_process_crop_package_id = this.hasOneItem(
        this.exporter_process_crop_packages
      );
    },
    fetchExporterProcessBrands: async function () {
      const response = await RequesterQuery.fetchExporterProcessBrands(
        this.organization_id,
        this.harvested_batch_id
      );
      this.exporter_process_brands = response.data;
      this.pkg.exporter_process_brand_id = this.hasOneItem(
        this.exporter_process_brands
      );
    },
    fetchPackingHouseProcesses: async function () {
      const response = await RequesterQuery.fetchPackingHouseProcesses(
        this.organization_id,
        this.harvested_batch_id
      );
      this.packing_house_processes = response.data;
      this.pkg.exported_batch.packing_house_process_id = this.hasOneItem(
        this.packing_house_processes
      );
    },
    fetchOrganizationCropField: async function () {
      const response = await RequesterQuery.fetchOrganizationCropField(
        this.organization_id,
        this.pkg.exported_batch.crop_field_id
      );
      if (!this.pkg.packing_line_id)
        this.pkg.packing_line_id = response.data.packing_line_id;
    },
    fetchCropFieldCertifications: async function () {
      const response = await RequesterQuery.fetchCropFieldCertifications(
        this.organization_id,
        this.pkg.exported_batch.crop_field_id
      );
      this.crop_field_certifications = response.data.sort(
        (a, b) =>
          this.availableCertification(a) - this.availableCertification(b)
      );
    },
    create: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.create(
          this.organization_id,
          this.harvested_batch_id,
          this.pkg
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowHarvestedBatch();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.charging_submit = false;
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    },
    availableCertification: function (certification) {
      return (
        this.daysUntilExpirationDate(certification.expiration_date) < 0 ||
        certification.suspended
      );
    },
    goToShowHarvestedBatch: function () {
      this.$router.push({
        name: this.routeName("show_harvested_batch"),
        params: {
          organization_id: this.organization_id,
          harvested_batch_id: this.harvested_batch_id,
        },
      });
    },
  },
  watch: {
    producer_crop_field_id: function (new_value) {
      if (!new_value) {
        this.pkg.producer_id = null;
        this.pkg.exported_batch.crop_field_id = null;

        return;
      }
      let ids = new_value.split("_");
      this.pkg.producer_id = ids[0];
      this.pkg.exported_batch.crop_field_id = ids[1];
    },
    "pkg.exported_batch.crop_field_id": function (new_value) {
      this.pkg.packing_line_id = null;
      if (new_value) {
        this.fetchOrganizationCropField();
        this.fetchCropFieldCertifications();
      }
    },
    "pkg.buy_price_currency_id": function (new_value) {
      this.pkg.buy_price_currency_id = new_value;
      if (new_value)
        this.currency_symbol = this.currencies.find(
          (currency) => currency.id == new_value
        )?.symbol;
    },
    "pkg.exporter_process_crop_package_id": function (
      exporter_process_crop_package_id
    ) {
      if (!exporter_process_crop_package_id) {
        this.pkg.exporter_process_brand_id = null;
        this.pkg.buy_price_per_package = null;
        return;
      }

      let exporter_process_corp_package =
        this.exporter_process_crop_packages.find(
          (exp_process_crop_package) =>
            exp_process_crop_package.id == exporter_process_crop_package_id
        );

      if (exporter_process_corp_package) {
        this.pkg.exporter_process_brand_id =
          exporter_process_corp_package?.exporter_process_brand_id;
        let buy_price =
          exporter_process_corp_package.export_process_package_buy_prices.find(
            (buy_price) => buy_price.default
          );
        this.pkg.buy_price_currency_id = buy_price?.currency_id;
        this.pkg.buy_price_per_package = buy_price?.price_per_package;
      }
    },
  },
};
</script>
